// ============================================================================
// Project Costing Sheet - Phase 16 of method-first pipeline
// ----------------------------------------------------------------------------
// Consolidates the project's scope-item costBreakdowns into an activity-
// grouped cost plan for ADMIN / OPS. This is the cost-focused sibling of
// the Project WMS document - same activities, same methodology, but
// structured for cost control (budget vs actual tracking) rather than
// field execution.
//
// Why it exists:
//   The method is the spine of the quote. The same method is viewed
//   multiple ways - scope items for the client, WMS for contractors,
//   costing sheet for admin. Admin's view focuses on: where is the money
//   going, what do we need to buy, who's costing what, what's the
//   variance once work starts.
//
// Design decisions:
//   1. DETERMINISTIC consolidation - no AI pass required for the core
//      aggregation. Cheap, fast, reliable. Existing cost_breakdown data
//      is already rich enough.
//   2. AI-enhanced narrative sections (contingency_rationale, risk_flags)
//      are OPTIONAL and left as placeholders for a follow-up session.
//   3. Preserves `actuals` across regenerations so admin's hand-entered
//      actual spend doesn't get wiped when the budget is refreshed.
//   4. Matches WMS activity IDs so the two documents can be cross-read -
//      section 3.2 of the WMS maps to line 3.2 of the costing sheet.
//   5. Staleness detected via scope_hash (same mechanism as WMS).
//
// Data lives at `project.costingSheet`. Dexie auto-serialises on the
// project envelope.
// ============================================================================

import { auditProjectCoherence, summariseCoherence } from '../utils/method-cost-coherence';
import { calculateProjectTotals } from './cost-math';
import { applyHbcf } from './cost-rules';
import { mapScopeToInputs, buildCostConfig, isCostMathV2Enabled } from './cost-math-adapter';
import { ratesheetFingerprint } from './ratesheet-fingerprint';
import { buildStage4PricingLedger } from '../utils/quote-pricing-readiness';
import {
    calculateHbcfEligibilityBaseIncGst,
    hasBuilderStatutoryAmountOverride,
    isManagedComplianceRow,
    projectFrancisHbcf,
    reconcileFrancisComplianceScope,
} from './rj-bird-pricing-methodology';
import { buildInternalPricingLedger } from './internal-pricing-ledger';
import { fallbackBusinessName } from './demo-identity';
import { deriveQuoteDisplayTotalsFromScope } from '../utils/quote-display-totals';
import { pricingPageResolvedMethodology } from '../../backend/src/shared/quote-brain/rate-book-core.js';

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Generate the project costing sheet from current scope. Deterministic -
 * returns a fully-populated structure suitable for rendering without any
 * network call.
 *
 * @param {Object} params
 * @param {Array}  params.scopeItems       scope with per-item costBreakdown + workMethod
 * @param {Object} params.formData         address, client, strata plan, quote ref
 * @param {Object} [params.projectStrategy] chosen approach (for project overview section)
 * @param {Object} [params.projectWMS]      current project WMS ledger (for coherence audit)
 * @param {Object} [params.overallProgramme] WMS programme (for programme summary)
 * @param {Object} [params.previousCostingSheet] last version - actuals preserved across regens
 * @param {Object} [params.costingConfig]   Pricing > Cost Model config for cost-math totals
 * @param {string} [params.businessName]   PCBU name for header
 * @param {boolean} [params.gstInclusive]  whether the quote totals include GST
 * @param {Object|null} [params.businessPricing] live Pricing snapshot; adds the
 *   rate-sheet hash to pricing_fingerprint (F-05)
 * @param {string|null} [params.qmQuoteNo] the QM quote number assigned to the
 *   project: a string is the known number, null means the project holds none,
 *   undefined (omitted) keeps the previous receipt's number
 * @returns {{ version: string, generated_at: string, scope_hash: string,
 *   pricing_fingerprint: (string|null), ratesheet_fingerprint: (string|null),
 *   content_fingerprint: (string|null), structured: any, generated_by: string }}
 */
export function generateProjectCostingSheet({
    scopeItems = [],
    formData = {},
    projectStrategy = null,
    projectWMS = null,
    overallProgramme = null,
    previousCostingSheet = null,
    costingConfig = null,
    // RJ Bird's name, or a demo business's own (services/demo-identity.ts).
    businessName = fallbackBusinessName(),
    gstInclusive = false,
    // F-05 (PRICING-AUDIT 2026-05-08): when callers pass the live
    // `businessPricing` snapshot, the stored sheet's `pricing_fingerprint`
    // includes the rate-sheet hash. Legacy callers that omit it still get a
    // valid (rate-sheet-blind) fingerprint — so old code paths don't break,
    // they just don't gain the rate-sheet drift detection.
    businessPricing = null,
    // The QM quote number ASSIGNED to this project (quoteData.meta.qmQuoteNo),
    // bound into the saved receipt so the staff export names the job even when
    // the client gave no order number or address. A string is the known number;
    // null means the caller knows there is none; undefined means the caller does
    // not know, and the previous receipt's number is kept rather than lost. It is
    // never derived from the address, the title or another project.
    qmQuoteNo = undefined,
} = {}) {
    const scope = Array.isArray(scopeItems) ? scopeItems : [];
    // Job costing records the cost lines the items carry. Since build-up
    // pricing step 2 (2026-11-03) it no longer certifies them against current
    // Pricing: nothing re-costs a line, and the sheet decides no price.
    // HBCF is advisory unless the operator/AI has made it visible as normal,
    // editable scope content. QuoteMaker must not add hidden client-facing
    // inclusions or generated admin rows that the operator cannot edit.
    const hbcfDisposition = _costingHbcfDisposition(formData);
    const hbcfWaived = hbcfDisposition.hbcfWaived;
    const hbcfIncludedInScope = hbcfDisposition.hbcfIncludedInScope;
    // Compute the default methodology even when the builder excludes HBCF.
    // The charged projection is zero in that disposition, but losing the
    // default premium/application receipt makes the work-order export unable
    // to explain what was deliberately waived.
    const methodologyHbcfResult = _resolveHbcfForScope(scope, gstInclusive, costingConfig);
    const rawHbcfResult = hbcfWaived
        ? {
            ...methodologyHbcfResult,
            required: false,
            amount: 0,
            rate: 0,
            applicationFeeExGst: 0,
            applicationFeeIncGst: 0,
            totalClientAdditionIncGst: 0,
            lineItem: null,
            waived: true,
            includedInScope: false,
        }
        : methodologyHbcfResult;
    const hbcfResult = rawHbcfResult?.required
        ? { ...rawHbcfResult, lineItem: null, waived: false, includedInScope: hbcfIncludedInScope }
        : { ...rawHbcfResult, lineItem: null, includedInScope: false };
    const pricedScope = scope;

    // Build per-activity budget rows. Preserve any actuals on the previous
    // costing sheet that still match by activity id - an admin who's been
    // filling in actuals shouldn't lose them on a budget refresh.
    const prevActualsById = new Map();
    if (previousCostingSheet?.structured?.activities) {
        for (const a of previousCostingSheet.structured.activities) {
            if (a?.id && a.actual) prevActualsById.set(a.id, a.actual);
        }
    }

    let computedLineTotals = null;
    try {
        if (isCostMathV2Enabled()) {
            computedLineTotals = calculateProjectTotals(
                // Each item at its own amount: since build-up pricing step 2
                // no markup re-derives a price (Peter, 2026-11-03).
                mapScopeToInputs(pricedScope, {
                    isGstInclusive: !!gstInclusive,
                    useVisibleAmountOverride: true,
                }),
                buildCostConfig(costingConfig),
            )?.items || null;
        }
    } catch {
        computedLineTotals = null;
    }

    const internalPricingLedger = buildInternalPricingLedger(pricedScope, { isGstInclusive: !!gstInclusive });
    const activities = pricedScope.map((item, idx) => _buildActivityRow(
        item,
        idx,
        prevActualsById,
        computedLineTotals?.[idx],
        !!gstInclusive,
        internalPricingLedger.items[idx] || null,
    ));

    // Project summary - sum across activities.
    const summary = _buildProjectSummary(activities, gstInclusive, pricedScope, costingConfig);

    // Procurement rollup - aggregated lists for admin procurement.
    const materials_summary  = _rollupMaterials(scope);
    const labour_summary     = _rollupLabour(scope);
    const equipment_summary  = _rollupEquipment(scope);
    const subcontractors_summary = _rollupSubcontractors(scope);

    // Programme summary - drawn from WMS overallProgramme if present,
    // otherwise computed from per-item programme_days.
    const programme_summary = _buildProgrammeSummary(scope, overallProgramme);

    // No name means the business's profile is not known yet. Keep the name the
    // last version stored: storing '' changes content_fingerprint, so a plain
    // project open would save the project and the next open would flip the
    // name back (F11).
    const previousBusinessName = previousCostingSheet?.structured?.business?.name;
    const headerBusinessName = businessName
        || (typeof previousBusinessName === 'string' ? previousBusinessName : '')
        || businessName;

    const resolvedQmQuoteNo = qmQuoteNo === undefined
        ? _qmQuoteNoOf(previousCostingSheet?.structured?.project?.qm_quote_no)
        : _qmQuoteNoOf(qmQuoteNo);
    const structured = {
        document_title: 'Internal Pricing',
        version: _bumpVersion(previousCostingSheet?.version),
        project: {
            address: formData?.address || '',
            strata_plan: formData?.strataplan || null,
            client: formData?.clientCompany || '',
            client_contact: formData?.clientName || '',
            quote_ref: formData?.orderNumber || '',
            ...(resolvedQmQuoteNo ? { qm_quote_no: resolvedQmQuoteNo } : {}),
            prepared_date: new Date().toISOString().slice(0, 10),
        },
        business: { name: headerBusinessName },
        chosen_approach: projectStrategy?.chosen?.label || null,
        approach_narrative: projectStrategy?.chosen?.narrative || null,
        summary,
        activities,
        procurement: {
            materials_summary,
            labour_summary,
            equipment_summary,
            subcontractors_summary,
        },
        programme_summary,
        admin_notes: {
            // v1 stubs - an optional AI pass can enrich these in a follow-up.
            contingency_rationale: '',
            risk_flags: [],
            margin_note: 'Each item is costed at its quoted amount. Actual builder margin is reported only when final rate-book lines have complete cost-to-business coverage.',
            pricing_rules: {
                hbcf: {
                    required: !!hbcfResult?.required,
                    included_as_activity: !!hbcfResult?.lineItem,
                    included_in_scope: !!hbcfResult?.includedInScope,
                    amount: _round(hbcfResult?.amount || 0),
                    rate: hbcfResult?.rate || 0,
                    application_fee_ex_gst: _round(hbcfResult?.applicationFeeExGst || 0),
                    application_fee_inc_gst: _round(hbcfResult?.applicationFeeIncGst || 0),
                    total_client_addition_inc_gst: _round(hbcfResult?.totalClientAdditionIncGst || hbcfResult?.amount || 0),
                    methodology_id: hbcfResult?.methodologyId || null,
                    methodology_required: !!methodologyHbcfResult?.required,
                    methodology_premium_inc_gst: _round(methodologyHbcfResult?.amount || 0),
                    methodology_rate: methodologyHbcfResult?.rate || 0,
                    methodology_application_fee_ex_gst: _round(methodologyHbcfResult?.applicationFeeExGst || 0),
                    methodology_application_fee_inc_gst: _round(methodologyHbcfResult?.applicationFeeIncGst || 0),
                    methodology_total_inc_gst: _round(
                        methodologyHbcfResult?.totalClientAdditionIncGst
                        || methodologyHbcfResult?.amount
                        || 0,
                    ),
                    estimated: hbcfResult?.estimated === true,
                    waived: !!hbcfWaived,
                    waiver_reason: hbcfDisposition.hbcfWaiverReason,
                    waiver_confirmed_by: hbcfDisposition.hbcfWaiverConfirmedBy,
                    waiver_confirmed_at: hbcfDisposition.hbcfWaiverConfirmedAt,
                },
            },
            // Phase 17: method \u2194 cost coherence audit folded into the
            // costing sheet so admin sees drift at a glance. Each entry has
            // severity, message, fix - admin can escalate to the builder.
            //
            // v5.16.11 (Peter 2026-05-04): preserve `acknowledged` flags
            // across regenerations. Pre-fix, every regen (including the
            // 800ms auto-refresh on scope change) rebuilt issues fresh
            // without the acknowledgement \u2192 operator clicked "Acknowledge
            // and proceed", blocker came right back. Now: build a
            // (code, activity_code/scopeItemRef) \u2192 acknowledged-meta
            // lookup from the previous sheet, then carry it forward
            // onto matching new issues. Two issues match if both their
            // code AND scopeItemRef agree (or both lack one of those).
            coherence: (() => {
                const audit = auditProjectCoherence({ scopeItems: scope, projectStrategy, projectWms: projectWMS });
                // Lookup previous acknowledgements.
                //
                // v5.16.17 ROBUSTNESS FIX (Peter 2026-05-04: "Cost coherence
                // block warnings that I can't dismiss... it keeps coming
                // back"): the previous v5.16.11 key scheme used a single
                // `code|(scopeItemRef || activity_code)` key on each side.
                // Those keys drift when an issue carries scopeItemRef in
                // one regen but only activity_code in the next, OR for
                // orphan-class issues that carry neither (only `activityId`).
                // When the keys don't match, the carried ack is dropped →
                // the blocker comes right back no matter how many times
                // the operator clicks "Acknowledge and proceed".
                //
                // Robust fix: ditch keyed lookup. For each new issue, scan
                // the previous acked issues for a match — same `code` AND
                // at least one matching reference (scopeItemRef OR
                // activity_code OR activityId). Same-code issues with NO
                // references on either side also match (so global blockers
                // stay acked). This makes the matcher tolerant of any one
                // reference field being added or removed between regens.
                const previousAcked = [];
                const previousIssues = previousCostingSheet?.structured?.admin_notes?.coherence?.issues;
                if (Array.isArray(previousIssues)) {
                    for (const prev of previousIssues) {
                        if (!prev?.acknowledged) continue;
                        previousAcked.push({
                            code: prev.code || '',
                            scopeItemRef: prev.scopeItemRef || null,
                            activity_code: prev.activity_code || null,
                            activityId: prev.activityId || null,
                            ack: {
                                acknowledged: true,
                                acknowledgedAt: prev.acknowledgedAt || null,
                                acknowledgedBy: prev.acknowledgedBy || null,
                            },
                        });
                    }
                }
                const findCarriedAck = (issueCode, refs) => {
                    if (!previousAcked.length) return null;
                    const code = issueCode || '';
                    const refSet = refs.filter(Boolean);
                    for (const prev of previousAcked) {
                        if (prev.code !== code) continue;
                        const prevRefs = [prev.scopeItemRef, prev.activity_code, prev.activityId].filter(Boolean);
                        // Both sides ref-less + same code → match (covers
                        // truly-global blockers).
                        if (refSet.length === 0 && prevRefs.length === 0) return prev.ack;
                        // At least one ref overlaps → match.
                        if (refSet.some(r => prevRefs.includes(r))) return prev.ack;
                    }
                    return null;
                };
                const flat = [];
                for (const [id, issues] of audit.perItem.entries()) {
                    const act = scope.find(it => (it?.id || '') === id);
                    const code = act?.code || id;
                    for (const iss of issues) {
                        const carriedAck = findCarriedAck(iss?.code || '', [
                            iss?.scopeItemRef, iss?.activity_code, iss?.activityId, code, id,
                        ]);
                        flat.push({
                            activity_code: code,
                            ...iss,
                            ...(carriedAck || {}),
                        });
                    }
                }
                for (const iss of audit.project) {
                    const carriedAck = findCarriedAck(iss?.code || '', [
                        iss?.scopeItemRef, iss?.activity_code, iss?.activityId,
                    ]);
                    flat.push({
                        activity_code: null,
                        ...iss,
                        ...(carriedAck || {}),
                    });
                }
                return {
                    summary: summariseCoherence(flat),
                    counts: audit.counts,
                    issues: flat,
                };
            })(),
        },
        footer: {
            prepared_date: new Date().toISOString().slice(0, 10),
            revision_note: previousCostingSheet
                ? `Regenerated from current scope. Previous version: ${previousCostingSheet.version || 'v1.0'}.`
                : 'Initial issue.',
            related_documents: [
                `Quote reference: ${formData?.orderNumber || '(unassigned)'}`,
                'Work Method: separate document - contractor brief',
                'Safe Work Method Statement (SWMS): separate document - required before site works',
            ],
        },
    };

    return {
        version: structured.version,
        generated_at: new Date().toISOString(),
        scope_hash: _costingScopeHash(scope),
        pricing_fingerprint: _costingPricingFingerprint(scope, {
            gstInclusive,
            costingConfig,
            businessPricing,
            ...hbcfDisposition,
        }),
        // Covers every stable value rendered/exported from `structured`, not
        // just scope and price inputs. Project identity, HBCF presentation,
        // chosen approach, programme, acknowledgements and saved actuals can
        // all change while scope_hash + pricing_fingerprint remain equal.
        // Volatile issue metadata is excluded by the helper so an identical
        // regeneration converges without a redundant project save.
        content_fingerprint: _costingContentFingerprint(structured),
        // F-05: surface the rate-sheet fingerprint as a separate field so
        // UI surfaces (Rate-Trace panel, QM Assistant) can display the
        // 12-char identifier without re-deriving it. Distinct from
        // `pricing_fingerprint`, which is the broader scope+config+ratesheet
        // hash used for staleness comparison.
        ratesheet_fingerprint: ratesheetFingerprint(businessPricing),
        structured,
        generated_by: 'deterministic',
    };
}

/**
 * Staleness check - matches the WMS pattern. Returns true when stored
 * costing sheet was generated against a different scope shape than
 * current. Pricing changes do invalidate the budget (unlike WMS, where
 * only method changes invalidate).
 */
export function isCostingStale(costingSheet, currentScope, pricingOptions = null) {
    if (!costingSheet || !costingSheet.scope_hash) return false;
    // A receipt that NAMES a different QM quote number than the project holds
    // now, or one the operator has since cleared, would present the wrong job in
    // the staff export (a duplicate that inherited its source's receipt, a
    // renumbered or cleared quote), so it is stale. A receipt that names none is
    // not stale here: it predates the number or the binding and presents no
    // wrong identity. costingReceiptNeedsIdentityBinding is the separate check
    // the refresh path uses to bind it. An identity the caller did not supply
    // (undefined) never makes a receipt stale; a supplied null or '' is a
    // reading ("none"), not an absence.
    if (costingReceiptIdentityConflicts(costingSheet, pricingOptions?.qmQuoteNo)) return true;
    // For costing sheet, we want a MORE sensitive staleness than WMS -
    // any cost_breakdown change counts (not just method shape). Use a
    // broader hash.
    const currentHash = _costingScopeHash(currentScope);
    if (costingSheet.scope_hash !== currentHash) return true;
    if (pricingOptions && typeof pricingOptions === 'object' && Object.keys(pricingOptions).length > 0) {
        const currentPricingFingerprint = _costingPricingFingerprint(currentScope, pricingOptions);
        if (!costingSheet.pricing_fingerprint) return true;
        return costingSheet.pricing_fingerprint !== currentPricingFingerprint;
    }
    return false;
}

/**
 * Strong Stage 3 → 5 costing proof.
 *
 * `isCostingStale(null, scope)` intentionally returns false for legacy UI
 * callers, so it cannot be used alone as a paid-boundary readiness check.
 * Auto quote generation requires a real deterministic sheet, its pricing
 * fingerprint, one activity per final scope row, and an exact scope hash.
 */
export function hasFreshCostingForScope(costingSheet, currentScope, pricingOptions = null) {
    if (!costingSheet || typeof costingSheet !== 'object') return false;
    if (!Array.isArray(currentScope) || currentScope.length === 0) return false;
    // Missing is not proof. Legacy/in-memory sheets must cross one successful
    // project-save boundary before they can unlock the paid Stage 5 author.
    if (costingSheet.persistence_verified !== true) return false;
    if (!costingSheet.scope_hash || !costingSheet.pricing_fingerprint) return false;
    const activities = costingSheet?.structured?.activities;
    if (!Array.isArray(activities) || activities.length !== currentScope.length) return false;
    return !isCostingStale(costingSheet, currentScope, pricingOptions);
}

/**
 * Stable identity for the exact commercial inputs consumed by a costing
 * regeneration. Stage 5 uses this to deduplicate automatic refreshes without
 * reducing a scope row to only its id and quoted amount.
 */
export function projectCostingPricingFingerprint(scopeItems = [], pricingOptions = {}) {
    return _costingPricingFingerprint(scopeItems, pricingOptions);
}

// v5.19.19 (Peter 2026-05-05, "professional advice" alignment): the source
// of truth for "is this quote 60 days or 90 days?". Mirrors the HBCF advisory
// rule: HBCF applies (required AND not waived) -> 90 days, otherwise 60 days.
// HBCF presentation itself must stay visible/editable scope content, not a
// hidden generated quote line.
export function resolveValidity({
    scope = [],
    gstInclusive = false,
    costingConfig = null,
    hbcfWaived = false,
    hbcfIncludedInScope = false,
} = {}) {
    try {
        const reqs = resolveCostingAdminRequirements(Array.isArray(scope) ? scope : [], {
            gstInclusive: !!gstInclusive,
            costingConfig: costingConfig || null,
            hbcfWaived: !!hbcfWaived,
            hbcfIncludedInScope: !!hbcfIncludedInScope,
        });
        return (reqs?.hbcf?.required && !reqs?.hbcf?.waived) ? '90 days' : '60 days';
    } catch {
        // Defensive: if probe fails (corrupted scope, missing config) emit
        // the shorter tier rather than null. 60 days is the safer default
        // because over-promising 90 days on a non-HBCF quote would be wrong.
        return '60 days';
    }
}

export function resolveCostingAdminRequirements(scopeItems = [], {
    gstInclusive = false,
    costingConfig = null,
    hbcfWaived = false,
    hbcfIncludedInScope = false,
} = {}) {
    // A true waiver means HBCF does not apply. included-in-scope is only true
    // when a caller explicitly marks visible scope content as carrying HBCF.
    const explicitlyWaived = !!hbcfWaived;
    const includedInScope = !explicitlyWaived && !!hbcfIncludedInScope;
    const rawHbcf = explicitlyWaived
        ? { required: false, amount: 0, rate: 0, waived: true, includedInScope: false }
        : _resolveHbcfForScope(
              Array.isArray(scopeItems) ? scopeItems : [],
              !!gstInclusive,
              costingConfig,
          );
    const hbcf = includedInScope && rawHbcf?.required
        ? { ...rawHbcf, lineItem: null, waived: false, includedInScope: true }
        : { ...rawHbcf, includedInScope: false };
    return {
        hbcf: {
            required: !!hbcf?.required,
            amount: _round(hbcf?.amount || 0),
            rate: hbcf?.rate || 0,
            applicationFeeExGst: _round(hbcf?.applicationFeeExGst || 0),
            applicationFeeIncGst: _round(hbcf?.applicationFeeIncGst || 0),
            totalClientAdditionIncGst: _round(hbcf?.totalClientAdditionIncGst || hbcf?.amount || 0),
            methodologyId: hbcf?.methodologyId || null,
            estimated: hbcf?.estimated === true,
            waived: !!hbcf?.waived,
            includedInScope: !!hbcf?.includedInScope,
        },
    };
}

/** A QM quote number as the receipt stores it: trimmed text, bounded, or ''. Only text is a number. */
function _qmQuoteNoOf(value) {
    return typeof value === 'string' ? value.trim().slice(0, 64) : '';
}

/**
 * How a saved receipt's identity compares with the QM quote number the project
 * holds now. `currentQmQuoteNo` is what the caller read from the quote:
 *   undefined          the caller could not ask (no quote in hand, or a legacy
 *                      caller that passes no identity): nothing to compare.
 *   non-empty text     the quote holds that number.
 *   null or ''         the quote was read and holds none (never assigned, or the
 *                      operator cleared it).
 * Statuses:
 *   'unknown'    the identity was not supplied: nothing to compare.
 *   'current'    receipt and quote agree (the same number, or both hold none).
 *   'unnamed'    the quote holds a number the receipt does not name (costed
 *                before the number was assigned or bound): it presents no wrong
 *                identity, but it can be bound.
 *   'mismatch'   the receipt names a DIFFERENT number (a duplicate's inherited
 *                receipt, a renumbered quote): it must not be presented.
 *   'withdrawn'  the receipt names a number the quote now holds none of (cleared
 *                after costing): it must not be presented as current either.
 */
export function costingReceiptIdentityStatus(costingSheet, currentQmQuoteNo) {
    // Only absence is "unknown". A non-text value that is not null carries no
    // reading of the quote's number, so it is treated the same way rather than
    // as a clear.
    if (currentQmQuoteNo === undefined) return 'unknown';
    if (currentQmQuoteNo !== null && typeof currentQmQuoteNo !== 'string') return 'unknown';
    const current = _qmQuoteNoOf(currentQmQuoteNo);
    const saved = _qmQuoteNoOf(costingSheet?.structured?.project?.qm_quote_no);
    if (!current) return saved ? 'withdrawn' : 'current';
    if (!saved) return 'unnamed';
    return saved === current ? 'current' : 'mismatch';
}

/**
 * True when the receipt names an identity the quote does not hold (a different
 * number, or a number the operator has since cleared): it must not be exported
 * or shown as the current quote's costing.
 */
export function costingReceiptIdentityConflicts(costingSheet, currentQmQuoteNo) {
    const status = costingReceiptIdentityStatus(costingSheet, currentQmQuoteNo);
    return status === 'mismatch' || status === 'withdrawn';
}

/**
 * True when regenerating the receipt would bind or correct its QM number
 * (unnamed, mismatched or withdrawn). Regenerating with the same reading of the
 * quote ends in 'current', so this cannot repeat.
 */
export function costingReceiptNeedsIdentityBinding(costingSheet, currentQmQuoteNo) {
    const status = costingReceiptIdentityStatus(costingSheet, currentQmQuoteNo);
    return status === 'unnamed' || status === 'mismatch' || status === 'withdrawn';
}

/**
 * The same receipt without a QM quote number it names. A duplicate project
 * inherits its source's saved costing for the money, the actuals and the
 * builder's edits, but it is a new quote with its own number (assigned when it
 * is sent), so it must not carry the source's. Nothing else changes; the
 * content fingerprint is re-stated so the receipt stays internally consistent.
 */
export function withoutCostingReceiptQmQuoteNo(costingSheet) {
    const project = costingSheet?.structured?.project;
    if (!project || typeof project !== 'object' || !Object.prototype.hasOwnProperty.call(project, 'qm_quote_no')) {
        return costingSheet;
    }
    const { qm_quote_no: _inherited, ...projectWithoutNumber } = project;
    const structured = { ...costingSheet.structured, project: projectWithoutNumber };
    return {
        ...costingSheet,
        structured,
        content_fingerprint: _costingContentFingerprint(structured),
    };
}

function _stableCanon(value) {
    if (Array.isArray(value)) return value.map(_stableCanon);
    if (value && typeof value === 'object') {
        return Object.keys(value)
            .sort()
            .reduce((out, key) => {
                const v = value[key];
                if (typeof v !== 'function' && v !== undefined) out[key] = _stableCanon(v);
                return out;
            }, {});
    }
    if (typeof value === 'number') return Number.isFinite(value) ? _round(value) : null;
    return value ?? null;
}

function _hashString(value) {
    let h = 5381;
    for (let i = 0; i < value.length; i++) h = (((h << 5) + h) + value.charCodeAt(i)) | 0;
    return (h >>> 0).toString(16).padStart(8, '0');
}

function _costingContentFingerprint(structured) {
    const source = structured && typeof structured === 'object' ? structured : {};
    const canon = {
        ...source,
        // These values move by construction and do not alter the commercial
        // or operational content of the saved receipt.
        version: null,
        project: {
            ...(source.project || {}),
            prepared_date: null,
        },
        footer: {
            ...(source.footer || {}),
            prepared_date: null,
            revision_note: null,
        },
    };
    return _hashString(JSON.stringify(_stableCanon(canon)));
}

function _costingPricingFingerprint(scopeItems = [], pricingOptions = {}) {
    const options = pricingOptions && typeof pricingOptions === 'object'
        ? pricingOptions
        : {};
    const {
        gstInclusive = false,
        costingConfig = null,
        // F-05 (PRICING-AUDIT 2026-05-08): rate-sheet fingerprint embedded into
        // the canon so a mid-quote rate edit (e.g. Concreter $88 → $99) flips
        // `isCostingStale` to true. Optional for backward-compat: legacy callers
        // that omit `businessPricing` still produce a deterministic value (with
        // `ratesheetFp: null`); the existing missing-fingerprint guard keeps the
        // conservative behaviour.
        businessPricing = null,
    } = options;
    const scope = Array.isArray(scopeItems) ? scopeItems : [];
    const hbcfDisposition = _costingHbcfDisposition(options);
    const adminRequirements = resolveCostingAdminRequirements(scope, {
        gstInclusive,
        costingConfig,
        hbcfWaived: hbcfDisposition.hbcfWaived,
        hbcfIncludedInScope: hbcfDisposition.hbcfIncludedInScope,
    });
    const canon = {
        scopeHash: _costingScopeHash(scope),
        gstInclusive: !!gstInclusive,
        costingConfig: _stableCanon(buildCostConfig(costingConfig)),
        adminRequirements,
        hbcfDisposition,
        ratesheetFp: ratesheetFingerprint(businessPricing),
    };
    return _hashString(JSON.stringify(canon));
}

function _costingHbcfDisposition(source = {}) {
    const options = source && typeof source === 'object' ? source : {};
    const nestedFormData = options.formData && typeof options.formData === 'object'
        ? options.formData
        : {};
    const read = (key) => Object.prototype.hasOwnProperty.call(options, key)
        ? options[key]
        : nestedFormData[key];
    const cleanReceiptText = (value) => {
        if (typeof value !== 'string') return null;
        const trimmed = value.trim();
        return trimmed || null;
    };
    const hbcfWaived = read('hbcfWaived') === true;
    const hbcfIncludedInScope = !hbcfWaived && read('hbcfIncludedInScope') === true;
    return {
        hbcfWaived,
        hbcfIncludedInScope,
        // Receipt metadata only governs the active waiver. Clearing the waiver
        // must not leave stale historical text in the live pricing authority.
        hbcfWaiverReason: hbcfWaived ? cleanReceiptText(read('hbcfWaiverReason')) : null,
        hbcfWaiverConfirmedBy: hbcfWaived ? cleanReceiptText(read('hbcfWaiverConfirmedBy')) : null,
        hbcfWaiverConfirmedAt: hbcfWaived ? cleanReceiptText(read('hbcfWaiverConfirmedAt')) : null,
    };
}

function _isHbcfLine(item) {
    const haystack = `${item?.id || ''} ${item?.code || ''} ${item?.title || ''}`;
    return /hbcf|home\s+building\s+compensation/i.test(haystack);
}

function _complianceComponents(item) {
    const breakdown = item?.costBreakdown || item?.cost_breakdown || {};
    if (Array.isArray(breakdown.complianceComponents)) return breakdown.complianceComponents;
    if (Array.isArray(breakdown.compliance_components)) return breakdown.compliance_components;
    return [];
}

/**
 * The regulated-project HBCF premium lives inside a combined preliminaries
 * package. Re-project that deterministic package and read only its HBCF
 * components; treating the full package amount as the premium would also
 * count SRD/DCD, DBPA and contract-preparation charges as insurance.
 */
function _resolveCombinedComplianceHbcf(scope, pricingMethodology) {
    if (!pricingMethodology?.hbcf || !scope.some(isManagedComplianceRow)) return null;
    try {
        const projected = reconcileFrancisComplianceScope(scope, {
            methodology: pricingMethodology,
            // A persisted managed compliance row is itself the durable shape
            // of a confirmed regulated quote. The reconciliation rebuilds the
            // default component receipt while preserving any whole-package
            // builder amount override separately.
            compliancePosture: { status: 'confirmed_required' },
            hbcfWaived: false,
        });
        const combined = projected?.scopeItems?.find(isManagedComplianceRow);
        if (!combined) return null;
        const components = _complianceComponents(combined);
        const premiumComponents = components.filter(component => (
            String(component?.kind || '').trim().toLowerCase() === 'hbcf_premium_estimate'
        ));
        const applicationComponents = components.filter(component => (
            String(component?.kind || '').trim().toLowerCase() === 'hbcf_application'
        ));
        const gstRate = Math.max(0, Number(
            pricingMethodology.gst_rate ?? pricingMethodology.gstRate,
        ) || 0);
        const componentExGst = rows => rows.reduce((sum, component) => (
            sum + Math.max(0, Number(component?.amountExGst ?? component?.amount_ex_gst) || 0)
        ), 0);
        const premiumIncGst = _round(componentExGst(premiumComponents) * (1 + gstRate));
        const applicationFeeExGst = _round(componentExGst(applicationComponents));
        const applicationFeeIncGst = _round(applicationFeeExGst * (1 + gstRate));
        const required = premiumComponents.length > 0;
        return {
            required,
            amount: required ? premiumIncGst : 0,
            rate: Number(
                pricingMethodology.hbcf.premium_rate
                ?? pricingMethodology.hbcf.premiumRate,
            ) || 0,
            lineItem: null,
            applicationFeeExGst: required ? applicationFeeExGst : 0,
            applicationFeeIncGst: required ? applicationFeeIncGst : 0,
            totalClientAdditionIncGst: required
                ? _round(premiumIncGst + applicationFeeIncGst)
                : 0,
            methodologyId: pricingMethodology.id || null,
            estimated: true,
            alreadyVisible: required,
            includedInCombinedPackage: required,
        };
    } catch {
        return null;
    }
}

function _resolveHbcfForScope(scope, gstInclusive, costingConfig) {
    if (!Array.isArray(scope) || scope.length === 0) {
        return { required: false, amount: 0, rate: 0, lineItem: null };
    }
    // The business's own Pricing page method, or the starter method, as the
    // quote's HBCF rows follow it (rate-book-core.js pricingPageResolvedMethodology).
    const pricingMethodology = costingConfig && typeof costingConfig === 'object'
        ? pricingPageResolvedMethodology(costingConfig)
        : null;
    const combinedProjection = _resolveCombinedComplianceHbcf(scope, pricingMethodology);
    if (combinedProjection) return combinedProjection;
    const hbcfAlreadyVisible = scope.some((item) => _isHbcfLine(item));
    // HBCF applicability is tested against the quote before HBCF. Managed or
    // legacy visible HBCF rows must never feed their own premium base on reopen
    // or regeneration, otherwise the advisory compounds every pass.
    const preHbcfScope = scope.filter((item) => !_isHbcfLine(item));

    const visibleTotal = preHbcfScope.reduce((sum, item) => sum + _safeParseAmount(item?.amount), 0);
    let subtotalIncGst = visibleTotal > 0
        ? calculateHbcfEligibilityBaseIncGst(preHbcfScope, {
            isGstInclusive: !!gstInclusive,
            gstRate: 0.1,
        })
        : 0;
    try {
        if (!(subtotalIncGst > 0)) {
            const cm = calculateProjectTotals(
                mapScopeToInputs(preHbcfScope, { isGstInclusive: !!gstInclusive }),
                buildCostConfig(costingConfig),
            );
            subtotalIncGst = Number(cm?.totalIncGst ?? cm?.subtotalIncGst);
        }
    } catch {
        subtotalIncGst = 0;
    }

    if (!(subtotalIncGst > 0)) {
        subtotalIncGst = gstInclusive ? visibleTotal : visibleTotal * 1.1;
    }

    const francisHbcf = pricingMethodology?.hbcf
        ? projectFrancisHbcf(subtotalIncGst, pricingMethodology)
        : null;
    const hbcf = francisHbcf
        ? {
            required: francisHbcf.required,
            amount: francisHbcf.premiumIncGst,
            rate: francisHbcf.premiumRate,
            lineItem: null,
            applicationFeeExGst: francisHbcf.applicationFeeExGst,
            applicationFeeIncGst: francisHbcf.applicationFeeIncGst,
            totalClientAdditionIncGst: francisHbcf.totalClientAdditionIncGst,
            methodologyId: pricingMethodology.id || null,
            estimated: francisHbcf.estimated,
        }
        : applyHbcf({ subtotalIncGst, config: buildCostConfig(costingConfig) });
    if (hbcfAlreadyVisible) {
        return { ...hbcf, lineItem: null, alreadyVisible: true };
    }
    if (!hbcf.required || !hbcf.lineItem) return { ...hbcf, lineItem: null };
    return {
        ...hbcf,
        lineItem: _buildHbcfCostingItem(hbcf),
    };
}

function _buildHbcfCostingItem(hbcf) {
    const premium = _round(hbcf?.amount || 0);
    return {
        ...(hbcf?.lineItem || {}),
        id: 'hbcf-compliance',
        code: 'HBCF',
        title: 'HBCF. Home Building Compensation Fund Cover',
        amount: premium.toFixed(2),
        amountLocked: true,
        gstExempt: true,
        excludeFromFixedAllocations: true,
        isAdminItem: true,
        adminKind: 'hbcf',
        costBreakdown: {
            costSource: 'statutory-admin',
            directCostTotal: premium,
            contingencyAmount: 0,
            overheadAmount: 0,
            totalCost: premium,
            marginAmount: 0,
            marginPct: 0,
            materials: [],
            labour: [],
            equipment: [],
            subcontractors: [],
            otherCosts: [{ description: 'Home Building Compensation Fund premium', total: premium }],
        },
    };
}

// ─── Internal: per-activity row ───────────────────────────────────────────────

// Persist only the bounded scalar part of the match-time pricing verdict.
// Internal Price Working must be a projection of the saved receipt; it must not
// consult the live Pricing page later and silently change provenance after a
// rate-card rename or revision. Candidate arrays, generic provenance objects and
// full market-evidence payloads deliberately stay out of costing_sheet JSONB.
const LINE_AUDIT_TEXT_FIELDS = Object.freeze([
    ['pricingItemId', 'pricing_item_id', 160],
    // An unresolved direct lookup keeps the operator/model's requested row as
    // audit provenance only. It must remain distinct from canonical
    // pricingItemId, which stays null until the line is commercially valid.
    ['requestedPricingItemId', 'requested_pricing_item_id', 160],
    ['_rateSource', '_rate_source', 40],
    ['pricingStatus', 'pricing_status', 40],
    ['unresolvedReason', 'unresolved_reason', 160],
    ['matchedName', 'matched_name', 500],
    ['matchedUnit', 'matched_unit', 120],
    ['matchedBy', 'matched_by', 80],
    ['confidence', 'confidence', 80],
    ['rateTaxBasis', 'rate_tax_basis', 40],
    ['costRateTaxBasis', 'cost_rate_tax_basis', 40],
    ['rateSourceRating', 'rate_source_rating', 80],
    ['rateSourceNote', 'rate_source_note', 1000],
]);
const LINE_AUDIT_NUMBER_FIELDS = Object.freeze([
    ['matchedRate', 'matched_rate'],
    ['costRate', 'cost_rate'],
]);
const LINE_AUDIT_BOOLEAN_FIELDS = Object.freeze([
    ['rateIsFinalChargeOut', 'rate_is_final_charge_out'],
    ['rateIsFinalIncGst', 'rate_is_final_inc_gst'],
    ['rateIncludesLabour', 'rate_includes_labour'],
]);

const PRICE_BUILD_UP_NUMBER_FIELDS = Object.freeze([
    ['pricing_band_multiplier', 'pricingBandMultiplier', 'pricing_band_multiplier'],
    ['rate_card_sell_ex_gst', 'rateCardSellExGst', 'rate_card_sell_ex_gst'],
    ['rate_card_banded_sell_ex_gst', 'rateCardBandedSellExGst', 'rate_card_banded_sell_ex_gst'],
    ['administration_share_ex_gst', 'administrationShareExGst', 'administration_share_ex_gst'],
    ['banded_administration_share_ex_gst', 'bandedAdministrationShareExGst', 'banded_administration_share_ex_gst'],
    ['minimum_attendance_allocation_inc_gst', 'minimumAttendanceAllocationIncGst', 'minimum_attendance_allocation_inc_gst'],
    ['unrounded_amount_inc_gst', 'pricingUnroundedIncGst', 'pricing_unrounded_inc_gst'],
    ['rounding_delta_inc_gst', 'pricingRoundingDeltaIncGst', 'pricing_rounding_delta_inc_gst'],
]);

function _boundedText(value, maxLength = 160) {
    const result = String(value ?? '').trim();
    return result ? result.slice(0, maxLength) : null;
}

function _nullableMoney(value) {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? _round(number) : null;
}

/**
 * Freeze the exact Stage 4 price bridge that existed when this costing sheet
 * was generated. Older exports must never be reconstructed from today's
 * mutable Pricing page or from bundled Francis defaults.
 */
function _buildPersistedPriceBuildUp(item, budget, gstInclusive) {
    const cb = item?.costBreakdown || item?.cost_breakdown || {};
    const methodologyId = _boundedText(cb.pricingMethodologyId ?? cb.pricing_methodology_id);
    const quoteAmountExGst = _nullableMoney(budget?.quoted_amount_ex_gst);
    const quoteGstAmount = _nullableMoney(budget?.quoted_gst_amount);
    const result = {
        receipt_status: methodologyId
            ? 'methodology-recorded'
            : 'legacy-partial',
        methodology_id: methodologyId,
        pricing_band: _boundedText(cb.pricingBand ?? cb.pricing_band, 80),
        administration_group_id: _boundedText(
            cb.administrationGroupId ?? cb.administration_group_id
            ?? item?.administrationGroupId ?? item?.administration_group_id,
            160,
        ),
        source_rating: _boundedText(item?.sourceRating ?? item?.source_rating, 80),
        source_note: _boundedText(item?.sourceNote ?? item?.source_note, 1000),
        amount_authority: _boundedText(item?.amountAuthority ?? item?.amount_authority, 80),
        quote_tax_basis: gstInclusive && item?.gstExempt !== true && item?.gst_exempt !== true
            ? 'inc_gst'
            : 'ex_gst',
        quote_amount_ex_gst: quoteAmountExGst,
        quote_gst_amount: quoteGstAmount,
        quote_amount_inc_gst: quoteAmountExGst == null || quoteGstAmount == null
            ? null
            : _round(quoteAmountExGst + quoteGstAmount),
        pricing_floor_ex_gst: _nullableMoney(budget?.pricing_floor_ex_gst),
    };
    for (const [outputKey, camelKey, snakeKey] of PRICE_BUILD_UP_NUMBER_FIELDS) {
        result[outputKey] = _nullableMoney(cb?.[camelKey] ?? cb?.[snakeKey]);
    }
    const postUnroundedAdjustment = result.rounding_delta_inc_gst;
    const minimumAttendance = result.minimum_attendance_allocation_inc_gst;
    result.post_unrounded_adjustment_inc_gst = postUnroundedAdjustment;
    result.rounding_only_inc_gst = postUnroundedAdjustment == null
        ? null
        : _round(postUnroundedAdjustment - (minimumAttendance || 0));
    result.reconciliation_delta_inc_gst = result.quote_amount_inc_gst == null
        || result.unrounded_amount_inc_gst == null
        || postUnroundedAdjustment == null
        ? null
        : _round(result.quote_amount_inc_gst - (result.unrounded_amount_inc_gst + postUnroundedAdjustment));
    result.reconciliation_status = result.reconciliation_delta_inc_gst == null
        ? 'partial'
        : Math.abs(result.reconciliation_delta_inc_gst) <= 0.01
            ? 'reconciled'
            : 'mismatch';
    return result;
}

function _buildPersistedComplianceComponents(item) {
    return _complianceComponents(item).map((component) => ({
        kind: _boundedText(component?.kind, 80),
        label: _boundedText(component?.label, 240),
        inclusion: _boundedText(component?.inclusion, 1000),
        amountExGst: _nullableMoney(component?.amountExGst ?? component?.amount_ex_gst),
        sourceRating: _boundedText(component?.sourceRating ?? component?.source_rating, 80),
    })).filter(component => component.kind || component.label || component.amountExGst != null);
}

function _buildPersistedStatutoryOverride(item) {
    const breakdown = item?.costBreakdown || item?.cost_breakdown || {};
    const source = breakdown.statutoryAmountOverride || breakdown.statutory_amount_override;
    if (!source || typeof source !== 'object') return null;
    return {
        generatedAmountIncGst: _nullableMoney(
            source.generatedAmountIncGst ?? source.generated_amount_inc_gst,
        ),
        builderAmountIncGst: _nullableMoney(
            source.builderAmountIncGst ?? source.builder_amount_inc_gst,
        ),
        editedAt: _boundedText(source.editedAt ?? source.edited_at, 80),
        editedBy: _boundedText(source.editedBy ?? source.edited_by, 160),
    };
}

function _buildActivityRow(item, idx, prevActualsById, computedLineTotal = null, gstInclusive = false, estimatorLedgerItem = null) {
    const cb = item?.costBreakdown || {};
    const wm = item?.workMethod || null;
    const id = item?.id || String(idx + 1);
    const code = item?.code || String(idx + 1);

    // Sum raw line totals by category. The existing costBreakdown already
    // carries directCostTotal / totalCost / contingency / overhead fields
    // so we consume those rather than re-derive.
    const materials_total = _sumLineTotals(cb.materials);
    const labour_total    = _sumLineTotals(cb.labour);
    const equipment_total = _sumLineTotals(cb.equipment);
    const subcontractors_total = _sumLineTotals(cb.subcontractors);
    const other_total     = _sumLineTotals(cb.otherCosts || cb.other || []);

    const direct_cost_total = Number.isFinite(cb.directCostTotal)
        ? cb.directCostTotal
        : (materials_total + labour_total + equipment_total + subcontractors_total + other_total);

    const contingency_amount = Number.isFinite(cb.contingencyAmount) ? cb.contingencyAmount : 0;
    const overhead_amount    = Number.isFinite(cb.overheadAmount) ? cb.overheadAmount : 0;
    const total_cost         = Number.isFinite(cb.totalCost)
        ? cb.totalCost
        : (direct_cost_total + contingency_amount + overhead_amount);

    // Quoted amount (what the client sees). Under cost-math v2, unlocked
    // scope items are derived from the full cost base, so use the same
    // computed line total that drives the quote bottom line. Fall back to the
    // stored legacy amount when the cost engine is disabled or unavailable.
    const computedQuotedAmount = computedLineTotal
        ? (gstInclusive ? Number(computedLineTotal.incGst) : Number(computedLineTotal.exGst))
        : NaN;
    const quoted_amount = _round(Number.isFinite(computedQuotedAmount)
        ? computedQuotedAmount
        : _safeParseAmount(item?.amount));
    const computedQuotedExGst = computedLineTotal ? Number(computedLineTotal.exGst) : NaN;
    const quoted_amount_ex_gst = _round(Number.isFinite(computedQuotedExGst)
        ? computedQuotedExGst
        : (gstInclusive && !item?.gstExempt ? quoted_amount / 1.1 : quoted_amount));
    const computedQuotedGst = computedLineTotal ? Number(computedLineTotal.gst) : NaN;
    const quoted_gst_amount = _round(Number.isFinite(computedQuotedGst)
        ? computedQuotedGst
        : (gstInclusive
            ? Math.max(0, quoted_amount - quoted_amount_ex_gst)
            : (item?.gstExempt ? 0 : quoted_amount_ex_gst * 0.10)));
    const computedMarginAmount = computedLineTotal ? Number(computedLineTotal.marginAmount) : NaN;
    const legacyModelledMarginAmount = _round(Number.isFinite(computedMarginAmount)
        ? computedMarginAmount
        : (quoted_amount_ex_gst - total_cost));
    const legacyModelledMarginPct = _round(total_cost > 0 ? (legacyModelledMarginAmount / total_cost) * 100 : 0);
    // FINAL Stage 4 pricing authority: rate-book lines are customer charge-out,
    // not builder cost. Actual margin is only knowable when those lines carry
    // cost-to-business rates; otherwise show null/unknown instead of a false
    // margin derived by subtracting charge-out from charge-out.
    const pricingLedgerItem = buildStage4PricingLedger([item], gstInclusive).items[0];
    const margin_amount = pricingLedgerItem?.actualBuilderCostExGst == null
        ? null
        : _round(quoted_amount_ex_gst - pricingLedgerItem.actualBuilderCostExGst);
    // Activity `margin_pct` is historically a markup-on-cost percentage (the
    // project summary's gross margin remains margin / revenue). Preserve that
    // contract while replacing the false mixed-cost numerator.
    const margin_pct = margin_amount == null || !(pricingLedgerItem?.actualBuilderCostExGst > 0)
        ? null
        : _round((margin_amount / pricingLedgerItem.actualBuilderCostExGst) * 100);

    // Preserve any admin-entered actuals from the previous regen.
    const actual = prevActualsById.get(id) || {
        materials_total: null,
        labour_total: null,
        equipment_total: null,
        subcontractors_total: null,
        other_total: null,
        actual_cost_total: null,
        variance: null,
        variance_pct: null,
        notes: '',
    };

    // Denormalise line items for the detail view - admin wants to see the
    // actual purchase / hire items, not just totals.
    const withEstimator = (line, category, lineIndex, estimatorOverride = null) => {
        const estimator = estimatorOverride || estimatorLedgerItem?.categories?.[category]?.[lineIndex];
        if (!estimator) return line;
        return {
            ...line,
            estimator_quantity: estimator.quantity,
            business_cost_rate_ex_gst: estimator.businessCostRateExGst,
            customer_sell_rate_ex_gst: estimator.sellRateExGst,
            business_cost_total_ex_gst: estimator.businessCostTotalExGst,
            customer_sell_total_ex_gst: estimator.sellTotalExGst,
            gross_profit_ex_gst: estimator.grossProfitExGst,
            gross_margin_pct: estimator.grossMarginPct,
            sell_authority: estimator.sellAuthority,
        };
    };
    const labourEstimatorRows = estimatorLedgerItem?.categories?.labour;
    const labourLines = Array.isArray(labourEstimatorRows)
        ? labourEstimatorRows.map((estimator, lineIndex) => {
            const { source, consolidatedFrom } = _labourSourceForEstimatorRow(cb.labour, estimator, lineIndex);
            return withEstimator({
                ..._leanCostLine(source, ['trade', 'hours', 'workers', 'qty', 'quantity', 'unit', 'rate', 'unitRate', 'unit_rate', 'duration_days', 'durationDays', 'billableQuantity', 'billable_quantity', 'total']),
                trade: estimator.label || source.trade,
                unit: estimator.unit || source.unit,
                // Which saved lines this one stands for, so a consolidated line
                // is never mistaken for a single saved line.
                ...(consolidatedFrom.length > 1
                    ? { consolidated_row_count: consolidatedFrom.length, source_row_indices: consolidatedFrom }
                    : {}),
            }, 'labour', lineIndex, estimator);
        })
        : (cb.labour || []).map((line, lineIndex) => withEstimator(
            _leanCostLine(line, ['trade', 'hours', 'workers', 'qty', 'quantity', 'unit', 'rate', 'unitRate', 'unit_rate', 'duration_days', 'durationDays', 'billableQuantity', 'billable_quantity', 'total']),
            'labour',
            lineIndex,
        ));
    const line_items = {
        materials: (cb.materials || []).map((m, lineIndex) => withEstimator(_leanCostLine(m, ['description', 'qty', 'quantity', 'unit', 'rate', 'unitRate', 'unit_rate', 'total']), 'materials', lineIndex)),
        labour: labourLines,
        // F-08 (PRICING-AUDIT 2026-05-08): expose qty + unit alongside
        // unitRate now that cost-recompute preserves them on output. Matches
        // the materials line-items shape so admin sees per-unit metering.
        equipment: (cb.equipment || []).map((e, lineIndex) => withEstimator(_leanCostLine(e, ['description', 'qty', 'quantity', 'unit', 'rate', 'unitRate', 'unit_rate', 'duration_days', 'durationDays', 'ownership', 'total']), 'equipment', lineIndex)),
        subcontractors: (cb.subcontractors || []).map((s, lineIndex) => withEstimator(_leanCostLine(s, ['trade', 'description', 'contractorName', 'supplierId', 'pricingItemId', 'hours', 'workers', 'qty', 'quantity', 'unit', 'rate', 'unitRate', 'unit_rate', 'duration_days', 'durationDays', 'billableQuantity', 'billable_quantity', 'total']), 'subcontractors', lineIndex)),
        other: (cb.otherCosts || cb.other || []).map((o, lineIndex) => withEstimator(_leanCostLine(o, ['description', 'qty', 'quantity', 'unit', 'rate', 'unitRate', 'unit_rate', 'total']), 'otherCosts', lineIndex)),
    };

    // Method notes that admin wants visible next to the cost: what approach
    // drove this line, who's needed, how the programme stacks up.
    const method_notes = wm ? {
        chosen_approach: wm.chosen_approach || '',
        trades_required: Array.isArray(wm.trades_required)
            ? wm.trades_required.map(t => typeof t === 'string' ? t : (t?.trade || ''))
            : [],
        access_strategy: wm.access_strategy || '',
        programme_days: wm.programme_days ?? null,
        // Carry standards so admin sees compliance posture alongside cost
        // lines without needing to cross-read the WMS (Phase 21 consistency
        // fix - the audit noticed Costing Sheet had no standards visibility).
        standards_applied: Array.isArray(wm.standards_applied) ? wm.standards_applied : [],
    } : null;

    return {
        id, code,
        title: item?.title || '(untitled)',
        clientScopeItem: item?.clientScopeItem === true || item?.client_scope_item === true,
        isAdminItem: item?.isAdminItem === true || item?.is_admin_item === true,
        adminKind: item?.adminKind || item?.admin_kind || null,
        systemManaged: _boundedText(item?.systemManaged ?? item?.system_managed, 120),
        gstExempt: item?.gstExempt === true || item?.gst_exempt === true,
        amountLockReason: _boundedText(item?.amountLockReason ?? item?.amount_lock_reason, 120),
        amountEditedAt: _boundedText(item?.amountEditedAt ?? item?.amount_edited_at, 80),
        amountEditedBy: _boundedText(item?.amountEditedBy ?? item?.amount_edited_by, 160),
        builderAmountOverride: hasBuilderStatutoryAmountOverride(item),
        statutoryAmountOverride: _buildPersistedStatutoryOverride(item),
        complianceComponents: _buildPersistedComplianceComponents(item),
        budget: {
            materials_total,
            labour_total,
            equipment_total,
            subcontractors_total,
            other_total,
            direct_cost_total,
            contingency_amount,
            overhead_amount,
            total_cost,
            // Backward-compatible raw projection above. These explicit fields
            // are the honest semantics for new consumers.
            total_cost_semantics: pricingLedgerItem?.rateBookChargeOutIncGst > 0
                ? 'legacy-projection-not-actual-builder-cost'
                : 'estimated-builder-cost',
            pricing_floor_ex_gst: pricingLedgerItem?.pricingFloorExGst || 0,
            rate_book_charge_out_inc_gst: pricingLedgerItem?.rateBookChargeOutIncGst || 0,
            rate_book_charge_out_ex_gst: pricingLedgerItem?.rateBookChargeOutExGst || 0,
            unmatched_cost_base_ex_gst: pricingLedgerItem?.unmatchedCostBaseExGst || 0,
            actual_builder_cost_ex_gst: pricingLedgerItem?.actualBuilderCostExGst ?? null,
            actual_cost_coverage: pricingLedgerItem?.actualCostCoverage || 'none',
            quoted_amount,
            quoted_amount_ex_gst,
            quoted_gst_amount,
            margin_amount,
            margin_pct,
            legacy_modelled_margin_amount: legacyModelledMarginAmount,
            legacy_modelled_margin_pct: legacyModelledMarginPct,
        },
        pricing_build_up: _buildPersistedPriceBuildUp(item, {
            quoted_amount_ex_gst,
            quoted_gst_amount,
            pricing_floor_ex_gst: pricingLedgerItem?.pricingFloorExGst || 0,
        }, gstInclusive),
        actual,
        line_items,
        method_notes,
        amountLocked: !!item?.amountLocked,
    };
}

function _buildProjectSummary(activities, gstInclusive, scopeItems = [], costingConfig = null) {
    // Phase 21 audit fix: one NaN in any activity.budget used to propagate
    // through the reduce, turning every project-level total into NaN. Guard
    // each accumulation with a `|| 0` so a corrupt activity only misses its
    // own contribution, not the entire roll-up.
    const n = (v) => (Number.isFinite(v) ? v : 0);
    const totals = activities.reduce((acc, a) => {
        acc.direct_cost_total += n(a.budget.direct_cost_total);
        acc.contingency_total += n(a.budget.contingency_amount);
        acc.overhead_total    += n(a.budget.overhead_amount);
        acc.total_cost        += n(a.budget.total_cost);
        acc.quote_total_display += n(a.budget.quoted_amount);
        acc.quote_total_ex_gst += n(a.budget.quoted_amount_ex_gst ?? a.budget.quoted_amount);
        acc.gst_amount += n(a.budget.quoted_gst_amount);
        acc.margin_total      += n(a.budget.margin_amount);
        return acc;
    }, { direct_cost_total: 0, contingency_total: 0, overhead_total: 0, total_cost: 0, quote_total_display: 0, quote_total_ex_gst: 0, gst_amount: 0, margin_total: 0 });

    // The quote's totals: the one GST-exempt-aware function Stage 4 and Stage 5
    // print (quote-totals.js), over each item's own amount, so job costing
    // cannot disagree with the quote by a cent (build-up pricing step 2).
    const quoteTotals = deriveQuoteDisplayTotalsFromScope(scopeItems, { isGstInclusive: !!gstInclusive });
    const quote_total_ex_gst = quoteTotals.subtotalExGst;
    const gst_amount = quoteTotals.gstAmount;
    const quote_total_inc_gst = quoteTotals.totalIncGst;
    const legacyGrossMarginPctFinal = quote_total_ex_gst > 0
        ? (totals.margin_total / quote_total_ex_gst) * 100
        : 0;
    const pricingLedger = buildStage4PricingLedger(scopeItems, !!gstInclusive);

    return {
        direct_cost_total: _round(totals.direct_cost_total),
        contingency_total: _round(totals.contingency_total),
        overhead_total: _round(totals.overhead_total),
        total_cost: _round(totals.total_cost),
        quote_total_ex_gst: _round(quote_total_ex_gst),
        gst_amount: _round(gst_amount),
        quote_total_inc_gst: _round(quote_total_inc_gst),
        // `total_cost` remains for persisted-sheet/backward compatibility. It
        // may be a legacy mixed projection when final rate-book charge-out is
        // present; new consumers must use the explicit pricing-floor / actual-
        // cost fields below.
        total_cost_semantics: pricingLedger.rateBookChargeOutIncGst > 0
            ? 'legacy-projection-not-actual-builder-cost'
            : 'estimated-builder-cost',
        pricing_floor_ex_gst: pricingLedger.pricingFloorExGst,
        pricing_floor_headroom: pricingLedger.pricingHeadroom,
        rate_book_charge_out_inc_gst: pricingLedger.rateBookChargeOutIncGst,
        rate_book_charge_out_ex_gst: pricingLedger.rateBookChargeOutExGst,
        unmatched_cost_base_ex_gst: pricingLedger.unmatchedCostBaseExGst,
        actual_builder_cost_ex_gst: pricingLedger.actualBuilderCostExGst,
        actual_cost_coverage: pricingLedger.actualCostCoverage,
        actual_margin_amount: pricingLedger.actualMarginAmount,
        actual_margin_pct: pricingLedger.actualMarginPercent,
        // Existing display fields now carry the honest actual-margin result.
        // Null is intentional when the tenant has not entered rate-book
        // costRate data. Keep the old modelled result under an explicit legacy
        // name so persisted/external consumers can migrate without data loss.
        gross_margin_amount: pricingLedger.actualMarginAmount,
        gross_margin_pct: pricingLedger.actualMarginPercent,
        legacy_modelled_margin_amount: _round(totals.margin_total),
        legacy_modelled_margin_pct: _round(legacyGrossMarginPctFinal),
    };
}

// ─── Internal: procurement rollups ────────────────────────────────────────────

/**
 * Aggregate materials across all activities. Groups by description so
 * admin gets ONE line per product to order, with total quantity summed
 * (when units match) or flagged for manual review (when units conflict).
 */
function _rollupMaterials(scope) {
    const buckets = new Map();
    for (const item of scope) {
        const mats = item?.costBreakdown?.materials || [];
        for (const m of mats) {
            if (!m || !m.description) continue;
            const key = String(m.description).trim().toLowerCase();
            if (!buckets.has(key)) {
                buckets.set(key, {
                    description: m.description,
                    total_qty: 0,
                    unit: m.unit || '',
                    estimated_cost: 0,
                    activity_codes: new Set(),
                    unit_mismatch: false,
                });
            }
            const b = buckets.get(key);
            if (Number.isFinite(m.qty)) b.total_qty += m.qty;
            if (Number.isFinite(m.total)) b.estimated_cost += m.total;
            if (m.unit && b.unit && m.unit !== b.unit) b.unit_mismatch = true;
            if (item?.code) b.activity_codes.add(item.code);
        }
    }
    return Array.from(buckets.values()).map(b => ({
        description: b.description,
        total_qty: b.unit_mismatch ? null : _round(b.total_qty),
        unit: b.unit_mismatch ? 'MIXED' : b.unit,
        estimated_cost: _round(b.estimated_cost),
        activity_codes: Array.from(b.activity_codes),
    })).sort((a, b) => b.estimated_cost - a.estimated_cost);
}

/**
 * Aggregate labour by trade. Total hours + total days (÷ 8h) + estimated
 * cost. Useful for crew scheduling and trade-contractor procurement.
 */
function _rollupLabour(scope) {
    const buckets = new Map();
    for (const item of scope) {
        const lab = item?.costBreakdown?.labour || [];
        for (const l of lab) {
            if (!l || !l.trade) continue;
            const key = String(l.trade).trim().toLowerCase();
            if (!buckets.has(key)) {
                buckets.set(key, {
                    trade: l.trade,
                    total_hours: 0,
                    estimated_cost: 0,
                    activity_codes: new Set(),
                });
            }
            const b = buckets.get(key);
            if (Number.isFinite(l.hours)) b.total_hours += l.hours;
            if (Number.isFinite(l.total)) b.estimated_cost += l.total;
            if (item?.code) b.activity_codes.add(item.code);
        }
    }
    return Array.from(buckets.values()).map(b => ({
        trade: b.trade,
        total_hours: _round(b.total_hours),
        total_days_equivalent: _round(b.total_hours / 8),
        estimated_cost: _round(b.estimated_cost),
        activity_codes: Array.from(b.activity_codes),
    })).sort((a, b) => b.estimated_cost - a.estimated_cost);
}

/**
 * Aggregate equipment by description. Lists all plant / hire gear to
 * procure across the project.
 */
function _rollupEquipment(scope) {
    // F-08 (PRICING-AUDIT 2026-05-08): consume `qty` / `unit` / `unitRate`
    // now that cost-recompute preserves them on equipment output. Mirrors the
    // materials rollup shape so admin procurement sees rate-per-unit and
    // detects unit conflicts across activities (e.g. "scaffold" priced as
    // "day" in one item and "week" in another — flagged MIXED).
    const buckets = new Map();
    for (const item of scope) {
        const eq = item?.costBreakdown?.equipment || [];
        for (const e of eq) {
            if (!e || !e.description) continue;
            const key = String(e.description).trim().toLowerCase();
            if (!buckets.has(key)) {
                buckets.set(key, {
                    description: e.description,
                    total_qty: 0,
                    unit: e.unit || '',
                    unit_rate_observed: Number.isFinite(e.unitRate) ? e.unitRate : null,
                    total_duration_days: 0,
                    total_hire_day_units: 0,
                    estimated_cost: 0,
                    activity_codes: new Set(),
                    unit_mismatch: false,
                });
            }
            const b = buckets.get(key);
            if (Number.isFinite(e.qty)) b.total_qty += e.qty;
            if (Number.isFinite(e.duration_days)) {
                b.total_duration_days += e.duration_days;
                b.total_hire_day_units += e.duration_days * (Number.isFinite(e.qty) ? e.qty : 1);
            }
            if (Number.isFinite(e.total)) b.estimated_cost += e.total;
            if (e.unit && b.unit && e.unit !== b.unit) b.unit_mismatch = true;
            if (item?.code) b.activity_codes.add(item.code);
        }
    }
    return Array.from(buckets.values()).map(b => ({
        description: b.description,
        total_qty: b.unit_mismatch ? null : _round(b.total_qty),
        unit: b.unit_mismatch ? 'MIXED' : b.unit,
        unit_rate_observed: b.unit_rate_observed != null ? _round(b.unit_rate_observed) : null,
        total_duration_days: _round(b.total_duration_days),
        total_hire_day_units: _round(b.total_hire_day_units),
        estimated_cost: _round(b.estimated_cost),
        activity_codes: Array.from(b.activity_codes),
    })).sort((a, b) => b.estimated_cost - a.estimated_cost);
}

/**
 * Aggregate subcontracted packages separately from internal labour so admin
 * can review package procurement, supplier allocation, and budget exposure.
 */
function _rollupSubcontractors(scope) {
    const buckets = new Map();
    for (const item of scope) {
        const subs = item?.costBreakdown?.subcontractors || [];
        for (const s of subs) {
            const trade = s?.trade || s?.description || s?.name;
            if (!trade) continue;
            const contractorName = String(s?.contractorName || s?.matchedContractorName || '').trim();
            const supplierId = String(s?.supplierId || s?.matchedSupplierId || '').trim();
            const key = [
                String(trade).trim().toLowerCase(),
                supplierId.toLowerCase(),
                contractorName.toLowerCase(),
            ].join('\u0000');
            if (!buckets.has(key)) {
                buckets.set(key, {
                    trade,
                    contractorName: contractorName || null,
                    supplierId: supplierId || null,
                    total_hours: 0,
                    estimated_cost: 0,
                    activity_codes: new Set(),
                });
            }
            const b = buckets.get(key);
            if (Number.isFinite(s.hours)) b.total_hours += s.hours;
            if (Number.isFinite(s.total)) b.estimated_cost += s.total;
            if (item?.code) b.activity_codes.add(item.code);
        }
    }
    return Array.from(buckets.values()).map(b => ({
        trade: b.trade,
        contractorName: b.contractorName,
        supplierId: b.supplierId,
        total_hours: _round(b.total_hours),
        estimated_cost: _round(b.estimated_cost),
        activity_codes: Array.from(b.activity_codes),
    })).sort((a, b) => b.estimated_cost - a.estimated_cost);
}

function _buildProgrammeSummary(scope, overallProgramme) {
    const perItemDays = scope
        .map(it => Number.isFinite(it?.workMethod?.programme_days) ? it.workMethod.programme_days : 0)
        .reduce((a, b) => a + b, 0);
    return {
        total_working_days: overallProgramme?.duration_working_days || perItemDays,
        critical_path: overallProgramme?.critical_path || '',
        sequence_summary: overallProgramme?.sequence_summary || '',
        source: overallProgramme ? 'wms-enrichment' : 'item-sum',
    };
}

// ─── Internal: small helpers ──────────────────────────────────────────────────

function _sumLineTotals(lines) {
    if (!Array.isArray(lines)) return 0;
    // Phase 21 audit fix: `Number("1,234.50")` returns NaN, silently zeroing
    // out any cost line stored as a comma-formatted string (which happens
    // when user-entered amounts pass through the UI). Use the same comma-
    // stripping parse as _safeParseAmount so the aggregation is stable
    // regardless of how the line's .total arrived.
    return lines.reduce((sum, l) => {
        const t = _safeParseAmount(l?.total);
        return Number.isFinite(t) ? sum + t : sum;
    }, 0);
}

function _leanLine(line, fields) {
    if (!line || typeof line !== 'object') return {};
    const out = {};
    for (const f of fields) if (line[f] !== undefined) out[f] = line[f];
    return out;
}

function _lineField(line, camelKey, snakeKey) {
    if (line?.[camelKey] !== undefined && line?.[camelKey] !== null) return line[camelKey];
    if (line?.[snakeKey] !== undefined && line?.[snakeKey] !== null) return line[snakeKey];
    return undefined;
}

// The numeric fields of a saved labour line that ADD across lines meaning the
// same thing (same label, unit, rate, authority and tax basis): the quantities,
// hours, days and the stored amount. Rates and flags do not add.
const ADDITIVE_LABOUR_FIELDS = Object.freeze([
    'qty', 'quantity', 'hours', 'duration_days', 'durationDays',
    'billableQuantity', 'billable_quantity', 'total',
]);

/** A stored amount as a finite number ("$1,234.50" reads as 1234.5), or null when it does not parse. */
function _amountOrNull(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const parsed = Number(String(value).replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The saved labour line a costing-sheet detail row is serialised from.
 *
 * The estimator ledger folds saved labour lines that mean the same thing into
 * ONE row (internal-pricing-ledger.js consolidateEquivalentLabourRows) and
 * names them in `sourceRowIndices`. Copying only the first of them left the
 * original quantity and total beside the aggregate estimator figures, so two
 * Painter $880 days exported as one $880 detail line while the category and
 * project totals still held both (2,964 detail against 3,844 direct). A row
 * that stands for several saved lines is serialised from their SUM, so the
 * detail lines add up to the totals and no cost is dropped. The ledger only
 * folds lines that share one stored shape (unit, rate, source, tax basis, crew
 * size and driver fields, every figure parseable), so each additive field is
 * summed with the same meaning on every line. Lines that differ in any of
 * those are never folded and stay separate rows with their own figures. A
 * figure that cannot be read is carried as unknown (null), never as zero.
 */
function _labourSourceForEstimatorRow(labourRows, estimator, lineIndex) {
    const rows = Array.isArray(labourRows) ? labourRows : [];
    const indices = Array.isArray(estimator?.sourceRowIndices) && estimator.sourceRowIndices.length > 0
        ? estimator.sourceRowIndices
        : [estimator?.rowIndex ?? lineIndex];
    const sources = indices.map(index => rows[index]).filter(row => row && typeof row === 'object');
    const first = sources[0] || {};
    if (sources.length < 2) return { source: first, consolidatedFrom: sources.length === 1 ? indices : [] };
    const merged = { ...first };
    for (const field of ADDITIVE_LABOUR_FIELDS) {
        const present = sources.filter(row => row[field] !== undefined && row[field] !== null && row[field] !== '');
        if (present.length === 0) continue;
        const parsed = present.map(row => _amountOrNull(row[field]));
        merged[field] = parsed.some(value => value === null)
            ? null
            : _round(parsed.reduce((sum, value) => sum + value, 0));
    }
    // Crew size is not summed: every folded line has the same crew (the ledger
    // only folds equal shapes), and the quantities above already count workers.
    return { source: merged, consolidatedFrom: indices };
}

function _leanCostLine(line, fields) {
    const out = _leanLine(line, fields);
    if (!line || typeof line !== 'object') return out;

    for (const [camelKey, snakeKey, maxLength] of LINE_AUDIT_TEXT_FIELDS) {
        const value = _lineField(line, camelKey, snakeKey);
        if (value === undefined || value === '') continue;
        out[camelKey] = String(value).slice(0, maxLength);
    }
    for (const [camelKey, snakeKey] of LINE_AUDIT_NUMBER_FIELDS) {
        const rawValue = _lineField(line, camelKey, snakeKey);
        if (rawValue === undefined || rawValue === '') continue;
        const value = Number(rawValue);
        if (Number.isFinite(value)) out[camelKey] = value;
    }
    for (const [camelKey, snakeKey] of LINE_AUDIT_BOOLEAN_FIELDS) {
        const value = _lineField(line, camelKey, snakeKey);
        if (typeof value === 'boolean') out[camelKey] = value;
    }

    const marketEvidence = line.marketPricingEvidence || line.market_pricing_evidence;
    const marketSourceUrl = _lineField(line, 'marketSourceUrl', 'market_source_url')
        ?? marketEvidence?.sourceUrl
        ?? marketEvidence?.source_url;
    if (marketSourceUrl) out.marketSourceUrl = String(marketSourceUrl).slice(0, 2048);
    return out;
}

function _safeParseAmount(a) {
    if (typeof a === 'number' && Number.isFinite(a)) return a;
    const n = parseFloat(String(a || '0').replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
}

function _round(n) {
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 100) / 100;
}

function _bumpVersion(previousVersion) {
    if (!previousVersion || typeof previousVersion !== 'string') return 'v1.0';
    const m = previousVersion.match(/^v(\d+)\.(\d+)$/);
    if (!m) return 'v1.0';
    const major = parseInt(m[1], 10);
    const minor = parseInt(m[2], 10) + 1;
    return `v${major}.${minor}`;
}

function _stableCostingHashValue(value, seen = new WeakSet()) {
    if (value === undefined) return '__undefined__';
    if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
    if (typeof value === 'number') return Number.isFinite(value) ? value : String(value);
    if (typeof value !== 'object') return String(value);
    if (seen.has(value)) return '__circular__';
    seen.add(value);
    if (Array.isArray(value)) {
        const result = value.map((entry) => _stableCostingHashValue(entry, seen));
        seen.delete(value);
        return result;
    }
    const result = {};
    for (const key of Object.keys(value).sort()) {
        result[key] = _stableCostingHashValue(value[key], seen);
    }
    seen.delete(value);
    return result;
}

function _commercialCostingLines(costBreakdown) {
    const categories = ['materials', 'labour', 'equipment', 'subcontractors', 'otherCosts', 'other'];
    return categories.flatMap((storedCategory) => {
        const rows = Array.isArray(costBreakdown?.[storedCategory]) ? costBreakdown[storedCategory] : [];
        return rows.map((line, rowIndex) => ({
            // Keep both canonical category semantics and exact stored identity.
            // `other` is a supported legacy alias but must not collide with a
            // genuine Equipment/Subcontractor line carrying the same total.
            category: storedCategory === 'other' ? 'otherCosts' : storedCategory,
            storedCategory,
            rowIndex,
            // Hash the complete persisted constituent line with sorted keys.
            // Quantity/rate/unit/crew/duration/provenance and property presence
            // all affect future editing and export, even when the line total is
            // unchanged, so a total-only digest is not commercially safe.
            line: _stableCostingHashValue(line),
        }));
    });
}

/**
 * Costing-specific scope hash - MORE sensitive than the WMS hash.
 * Invalidates on cost_breakdown changes too (not just method shape)
 * because admin needs to know when the budget is stale.
 */
function _costingScopeHash(scope) {
    if (!Array.isArray(scope)) return '0';
    const canon = scope.map(it => ({
        id: it?.id || '',
        code: it?.code || '',
        title: (it?.title || '').trim(),
        amount: _safeParseAmount(it?.amount),
        quotedExGst: _safeParseAmount(it?.quoted_amount_ex_gst ?? it?.amountExGst ?? it?.amount_ex_gst),
        quotedIncGst: _safeParseAmount(it?.quoted_amount_inc_gst ?? it?.amountIncGst ?? it?.amount_inc_gst),
        amountLocked: it?.amountLocked === true || it?.amount_locked === true,
        gstExempt: it?.gstExempt === true || it?.gst_exempt === true,
        provisionalSum: it?.provisionalSum === true || it?.provisional_sum === true,
        isAdminItem: it?.isAdminItem === true || it?.is_admin_item === true,
        adminKind: it?.adminKind || it?.admin_kind || '',
        excludeFromFixedAllocations: it?.excludeFromFixedAllocations === true || it?.exclude_from_fixed_allocations === true,
        pricingBasis: it?.pricingBasis || it?.pricing_basis || '',
        isGstInclusive: it?.isGstInclusive,
        cb: it?.costBreakdown ? {
            direct: it.costBreakdown.directCostTotal || 0,
            contingencyPct: it.costBreakdown.contingencyPct || 0,
            contingencyAmount: it.costBreakdown.contingencyAmount || 0,
            overheadPct: it.costBreakdown.overheadPct || 0,
            overheadAmount: it.costBreakdown.overheadAmount || 0,
            total: it.costBreakdown.totalCost || 0,
            marginPct: it.costBreakdown.marginPct || 0,
            marginAmount: it.costBreakdown.marginAmount || 0,
            targetMarginPct: it.costBreakdown.targetMarginPct || 0,
            costSource: it.costBreakdown.costSource || '',
            diagnosticCostAdjusted: it.costBreakdown.diagnosticCostAdjusted === true,
            matsCount: (it.costBreakdown.materials || []).length,
            labCount: (it.costBreakdown.labour || []).length,
            eqCount: (it.costBreakdown.equipment || []).length,
            subCount: (it.costBreakdown.subcontractors || []).length,
            matsHash: (it.costBreakdown.materials || []).map(m => `${m?.description || ''}:${m?.qty ?? ''}:${m?.total ?? ''}`).join('|'),
            labHash: (it.costBreakdown.labour || []).map(l => `${l?.trade || ''}:${l?.hours ?? ''}:${l?.total ?? ''}`).join('|'),
            eqHash: (it.costBreakdown.equipment || []).map(e => `${e?.description || ''}:${e?.total ?? ''}`).join('|'),
            subHash: (it.costBreakdown.subcontractors || []).map(s => `${s?.trade || s?.description || ''}:${s?.hours ?? ''}:${s?.qty ?? ''}:${s?.total ?? ''}`).join('|'),
            otherHash: (it.costBreakdown.otherCosts || it.costBreakdown.other || []).map(o => `${o?.description || ''}:${o?.total ?? ''}`).join('|'),
            commercialLines: _commercialCostingLines(it.costBreakdown),
            pricingMethodologyId: it.costBreakdown.pricingMethodologyId || it.costBreakdown.pricing_methodology_id || '',
            pricingBand: it.costBreakdown.pricingBand || it.costBreakdown.pricing_band || '',
            pricingBandMultiplier: _nullableMoney(it.costBreakdown.pricingBandMultiplier ?? it.costBreakdown.pricing_band_multiplier),
            administrationGroupId: it.costBreakdown.administrationGroupId || it.costBreakdown.administration_group_id || '',
            administrationShareExGst: _nullableMoney(it.costBreakdown.administrationShareExGst ?? it.costBreakdown.administration_share_ex_gst),
            bandedAdministrationShareExGst: _nullableMoney(it.costBreakdown.bandedAdministrationShareExGst ?? it.costBreakdown.banded_administration_share_ex_gst),
            rateCardSellExGst: _nullableMoney(it.costBreakdown.rateCardSellExGst ?? it.costBreakdown.rate_card_sell_ex_gst),
            rateCardBandedSellExGst: _nullableMoney(it.costBreakdown.rateCardBandedSellExGst ?? it.costBreakdown.rate_card_banded_sell_ex_gst),
            minimumAttendanceAllocationIncGst: _nullableMoney(it.costBreakdown.minimumAttendanceAllocationIncGst ?? it.costBreakdown.minimum_attendance_allocation_inc_gst),
            pricingUnroundedIncGst: _nullableMoney(it.costBreakdown.pricingUnroundedIncGst ?? it.costBreakdown.pricing_unrounded_inc_gst),
            pricingRoundingDeltaIncGst: _nullableMoney(it.costBreakdown.pricingRoundingDeltaIncGst ?? it.costBreakdown.pricing_rounding_delta_inc_gst),
            lineAuditHash: ['materials', 'labour', 'equipment', 'subcontractors', 'otherCosts', 'other']
                .flatMap(key => Array.isArray(it.costBreakdown[key]) ? it.costBreakdown[key] : [])
                .map(line => [
                    line?.pricingItemId ?? line?.pricing_item_id ?? '',
                    line?.requestedPricingItemId ?? line?.requested_pricing_item_id ?? '',
                    line?._rateSource ?? line?._rate_source ?? '',
                    line?.rateTaxBasis ?? line?.rate_tax_basis ?? '',
                    line?.costRate ?? line?.cost_rate ?? '',
                    line?.costRateTaxBasis ?? line?.cost_rate_tax_basis ?? '',
                    line?.rateIsFinalChargeOut ?? line?.rate_is_final_charge_out ?? '',
                    line?.rateIsFinalIncGst ?? line?.rate_is_final_inc_gst ?? '',
                    line?.rateIncludesLabour ?? line?.rate_includes_labour ?? '',
                    line?.marketSourceUrl ?? line?.market_source_url ?? line?.marketPricingEvidence?.sourceUrl ?? '',
                ].join(':'))
                .join('|'),
        } : null,
    }));
    const s = JSON.stringify(canon);
    let h = 5381;
    for (let i = 0; i < s.length; i++) h = (((h << 5) + h) + s.charCodeAt(i)) | 0;
    return (h >>> 0).toString(16).padStart(8, '0');
}
